import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { useQuery } from 'react-query';
import {
  chatbotGuestSignIn,
  fetchTerms,
  guestSignIn,
  requestPhoneAuth,
  requestValidatePhoneAuthNo,
} from '../../../apis/auth';
import useAuthStore from '../../../store/useAuthStore';
import Button from '../../atom/Button';
import strings from '../../../constants/strings.constants';
import TermsList from './components/TermsList';
import Input from '../../atom/Input';
import Select from '../../atom/Select';
import {
  filterNumberString,
  formatTelNumber,
  getNationOptions,
  getNationalityNoByCode,
  isEmail,
  isPhoneNum,
  parsePhoneNumber,
} from '../../../utils/utils';
import AppError from '../../../apis/models/common/res/AppError';
import useAlertStore from '../../../store/useAlertStore';
import useLanguageCodeStore, {
  LanguageCode,
} from '../../../store/useLanguageCodeStore';
import { GuestSignInBody } from '../../../apis/models/auth/req/GuestSignInBody';
import TermsDetailModal from './components/TermsDetailModal';
import { useSearchParams } from 'react-router-dom';
import { fetchNations } from '../../../apis/nation';
import Typo from '../../atom/Typo';
import colors from '../../../constants/colors.constants';
import {
  ChatbotChannel,
  ChatbotGuestSignInBody,
} from '../../../apis/models/auth/req/ChatbotGuestSignInBody';
import { withMask } from 'use-mask-input';
import moment from 'moment';
import { parseFirstName, parseLastName } from '../../../hard-coded/hardCoding';
import { CustomerPartnerTermsDto } from '../../../apis/terms';

interface AuthForm {
  phone: string;
  name: string;
  authNo: string;
  memo?: string;
  authTokenId: string;
  firstName: string;
  lastName: string;
  birth?: string;
  email?: string;
}

export interface AuthBottomSheetProps {
  visible?: boolean;
  onCancel?: () => void;
  onAuthComplete?: (token: string) => void;
}

function AuthBottomSheet(props: AuthBottomSheetProps) {
  const { onAuthComplete, onCancel, visible = false } = props;
  const { partner, setCustomer } = useAuthStore();
  const alphabetRegex = /^[A-Za-z]*$/;

  const validateBirthdate = (input: string): string | null => {
    if (input) {
      const date = moment(input, 'YYYYMMDD', true); // strict parsing

      if (!date.isValid()) {
        return strings.invalidBirthInputMessage;
      }

      const today = moment();
      const age = today.diff(date, 'years');

      if (date.isAfter(today)) {
        return strings.invalidBirthInputMessage;
      }

      if (age > 90) {
        return strings.invalidBirthInputMessage;
      }
    }

    return '';
  };

  const getInitialFirstName = () => {
    let firstName = searchParams.get('firstName') || '';

    if (firstName) {
      firstName = parseFirstName(partner?.id || 0, firstName.trim());
    }

    return firstName;
  };

  const getInitialLastName = () => {
    let lastName = searchParams.get('lastName') || '';

    if (lastName) {
      lastName = parseLastName(partner?.id || 0, lastName.trim());
    }

    return lastName;
  };

  const getInitialBirth = () => {
    let birth = searchParams.get('birth') || '';
    if (birth) {
      const isValid = validateBirthdate(birth) === '';
      console.log('isValidBirth', isValid);
      if (isValid) {
        return birth;
      }
    }
    return '';
  };

  const getGenderParam = () => {
    const gender = searchParams.get('gender') || '';
    const genderUpperCase = gender.toUpperCase();
    if (genderUpperCase === 'M' || genderUpperCase === 'F') {
      if (genderUpperCase === 'M') {
        return 'MALE';
      } else if (genderUpperCase === 'F') {
        return 'FEMALE';
      }
    }
    return undefined;
  };

  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') || '';
  const chatbotChannel =
    searchParams.get('chatbotChannel') ||
    searchParams.get('chatBotChannel') ||
    '';
  const userKey = searchParams.get('userKey') || '';

  const phoneQuery = searchParams.get('mobileNumber')
    ? searchParams.get('mobileNumber')?.replace(' ', '+') || ''
    : '';
  const { phoneNumber, countryCode: initialCountryCode } =
    parsePhoneNumber(phoneQuery);

  const initialFormData = {
    phone: phoneNumber,
    name,
    authNo: '',
    memo: '',
    authTokenId: '',
    firstName: getInitialFirstName(),
    lastName: getInitialLastName(),
    birth: getInitialBirth(),
    email: '',
  };
  const [formData, setFormData] = useState<AuthForm>(initialFormData);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const languageCode = useLanguageCodeStore((state) => state.languageCode);
  const authNoInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [requestingAuthNo, setRequestingAuthNo] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);
  const { showAlert } = useAlertStore();
  const [termsDetailModailVisible, setTermsDetailModalVisible] =
    useState(false);
  const [selectedTerms, setSelectedTerms] = useState<CustomerPartnerTermsDto>();
  const [countryCode, setCountryCode] = useState<string | null>(
    initialCountryCode,
  );

  const { data: termsList = [] } = useQuery({
    queryKey: `FETCH_TERMS_${languageCode}`,
    queryFn: async () => {
      if (partner) {
        return fetchTerms(
          partner.partnerCode,
          languageCode !== LanguageCode.ko ? languageCode : undefined,
        );
      }
    },
    retry: false,
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const { data: nationList = [] } = useQuery({
    queryKey: `FETCH_NATIONS`,
    queryFn: async () => {
      if (languageCode && languageCode !== LanguageCode.ko) {
        return fetchNations();
      }
    },
    retry: false,
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const nationOptions = useMemo(
    () => getNationOptions(nationList),
    [nationList],
  );

  useEffect(() => {
    if (
      nationList?.length &&
      languageCode &&
      !searchParams.get('mobileNumber')
    ) {
      const target = nationList.find((n) => n.languageCode === languageCode);
      if (target) {
        setCountryCode(target.code);
      }
    }
  }, [nationList, languageCode, searchParams]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        if (phoneInputRef.current && nameInputRef.current) {
          phoneInputRef.current.focus();
          nameInputRef.current.focus();
        }
      }, 500);
    }
  }, [visible, phoneInputRef]);

  const shouldHideAuthRequestButton = () => {
    if (!languageCode || languageCode === LanguageCode.ko) {
      return false;
    }

    const mode = getCustomerAuthMode();
    return (
      mode === 'CHATBOT' ||
      mode === 'NONE' ||
      mode === 'EMAIL' ||
      countryCode === 'CN' ||
      countryCode === 'cn'
    );
  };

  const getCustomerAuthMode = () => {
    if (partner && languageCode && languageCode !== LanguageCode.ko) {
      // SNS 챗봇 인증의 경우 SNS user key와 채널 정보가 모두 있어야 함
      const isValidChatbotQuery = !!(
        Object.values(ChatbotChannel).filter((c) => c === chatbotChannel)
          .length > 0 && userKey
      );
      if (partner.chatBotConfig?.globalCustomerAuthMode === 'NONE') {
        return 'NONE';
      }
      if (partner.chatBotConfig?.globalCustomerAuthMode === 'EMAIL') {
        return 'EMAIL';
      }
      if (partner.chatBotConfig?.globalCustomerAuthMode === 'PHONE') {
        return 'PHONE';
      } else if (
        partner.chatBotConfig?.globalCustomerAuthMode === 'CHATBOT' &&
        isValidChatbotQuery
      ) {
        return 'CHATBOT';
      } else if (
        partner.chatBotConfig?.globalCustomerAuthMode === 'BIRTH' &&
        isValidChatbotQuery
      ) {
        return 'BIRTH';
      }
    }
    return 'PHONE';
  };

  const getBirthAuthDefaultPhoneNumber = () => {
    if (partner && partner.chatBotConfig) {
      const { defaultPhoneForBirthAuth } = partner.chatBotConfig;
      return defaultPhoneForBirthAuth || '010';
    }
    return '010';
  };

  const customerAuthMode = getCustomerAuthMode();

  const requestAuth = async (key: string) => {
    setRequestingAuthNo(true);
    try {
      let partnerCode = '';
      if (partner) {
        partnerCode = partner.partnerCode;
      }
      const isEmailAuth = customerAuthMode === 'EMAIL';
      const { authTokenId } = await requestPhoneAuth(partnerCode, {
        languageCode,
        key: isEmailAuth ? key : `${key.replaceAll('-', '')}`,
        payload: {},
        nationalityNo: getNationalityNoByCode(nationList, countryCode),
        type: isEmailAuth ? 'email' : 'message',
      });

      setFormData({
        ...formData,
        authTokenId,
        authNo:
          process.env.REACT_APP_ENV === 'dev' ||
          process.env.REACT_APP_ENV === 'local'
            ? '000531'
            : '',
      });

      if (isEmailAuth) {
        setResendTimer(30);
      }

      showAlert(
        isEmailAuth
          ? strings.feedbackAuthRequestSuccessEmail
          : strings.feedbackAuthRequestSuccess,
        () => {
          setTimeout(() => {
            if (authNoInputRef.current) {
              authNoInputRef.current.focus();
            }
          }, 100);
        },
      );
    } catch (e) {
      const error = e as AppError;
      if (error.message) {
        showAlert(error.message, () => {});
      }
    } finally {
      setRequestingAuthNo(false);
    }
  };

  const canRequestAuth = useCallback(() => {
    return (
      (!countryCode || countryCode === 'KR'
        ? isPhoneNum(formData.phone)
        : !!formData.phone) &&
      (formData.name || (formData.firstName && formData.lastName))
    );
  }, [countryCode, formData]);

  const canRequestEmailAuth = useCallback(() => {
    return canRequestAuth() && isEmail(formData.email || '');
  }, [canRequestAuth, formData.email]);

  const canSubmit = useCallback(() => {
    let isNameValid = false;
    if (!languageCode || languageCode === 'ko') {
      isNameValid = !!formData.name;
    } else {
      isNameValid = !!formData.firstName && !!formData.lastName;
    }

    // 해외 고객 생년월일 인증
    if (customerAuthMode === 'BIRTH') {
      return (
        termsAgreed &&
        !loading &&
        isNameValid &&
        validateBirthdate(formData.birth || '') === ''
      );
    }

    // 해외 고객 SNS 챗봇 인증 / NONE(문자 인증 생략)
    if (
      customerAuthMode === 'CHATBOT' ||
      customerAuthMode === 'NONE' ||
      countryCode === 'CN' ||
      countryCode === 'cn'
    ) {
      return termsAgreed && !loading && !!formData.phone && isNameValid;
    }

    // 국내 또는 일반 외국인 고객 인증
    return (
      (!countryCode || countryCode === 'KR'
        ? isPhoneNum(formData.phone)
        : !!formData.phone) &&
      isNameValid &&
      formData.authTokenId &&
      formData.authNo &&
      termsAgreed &&
      !loading
    );
  }, [
    countryCode,
    formData,
    loading,
    termsAgreed,
    languageCode,
    customerAuthMode,
  ]);

  const requestSignIn = async () => {
    try {
      const { authTokenId, authNo, name, phone, firstName, lastName } =
        formData;

      let nameParam = name;
      if (languageCode && languageCode !== LanguageCode.ko) {
        nameParam = `${lastName} ${firstName}`;
      }

      setLoading(true);
      // 인증번호 검증
      const { accessToken } = await requestValidatePhoneAuthNo({
        authTokenId,
        token: authNo,
        nationalityNo: getNationalityNoByCode(nationList, countryCode),
        type: customerAuthMode === 'EMAIL' ? 'email' : 'message',
      });
      // 임시 로그인 처리
      const signInBody: GuestSignInBody = {
        accessToken: accessToken,
        authTokenId: authTokenId,
        customerName: nameParam,
        customerPhone: phone.replaceAll('-', ''),
        partnerId: partner?.id || 0,
      };
      const customer = await guestSignIn(signInBody);
      setCustomer(customer);

      if (onAuthComplete) onAuthComplete(customer.token);
      if (onCancel) onCancel();
    } catch (e) {
      const error = e as AppError;
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const requestChatbotSignIn = async () => {
    try {
      const { name, phone, firstName, lastName, birth } = formData;

      let nameParam = name;
      if (languageCode && languageCode !== LanguageCode.ko) {
        nameParam = `${lastName} ${firstName}`;
      }

      let phoneNumberParam = phone.replaceAll('-', '');
      if (customerAuthMode === 'BIRTH') {
        phoneNumberParam = getBirthAuthDefaultPhoneNumber();
      }
      let nationalityNoParam = getNationalityNoByCode(nationList, countryCode);
      if (customerAuthMode === 'BIRTH') {
        nationalityNoParam = '+1';
      }
      const genderParam = getGenderParam();

      setLoading(true);
      // 임시 로그인 처리
      const signInBody: ChatbotGuestSignInBody = {
        customerName: nameParam,
        customerPhone: phoneNumberParam,
        partnerId: partner?.id || 0,
        chatbotChannel: (chatbotChannel as ChatbotChannel) || undefined,
        userKey: userKey || undefined,
        nationalityNo: nationalityNoParam,
        birth: birth || undefined,
        gender: genderParam,
      };
      const customer = await chatbotGuestSignIn(signInBody);
      setCustomer(customer);
      if (onAuthComplete) onAuthComplete(customer.token);
      if (onCancel) onCancel();
    } catch (e) {
      const error = e as AppError;
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sheet
        avoidKeyboard
        detent="content"
        isOpen={visible}
        onClose={() => {
          if (onCancel) onCancel();
        }}
        style={{
          zIndex: 10,
        }}
        tweenConfig={{ ease: 'easeOut', duration: 0.4 }}
      >
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content>
            <div className="p-[20px]">
              <div className="flex flex-col gap-2 mb-3">
                {languageCode && languageCode === LanguageCode.ko ? (
                  // 국내 고객 이름 입력
                  <Input
                    ref={nameInputRef}
                    style={{
                      width: '100%',
                      height: '48px',
                      borderRadius: '12px',
                    }}
                    value={formData.name}
                    placeholder={strings.namePlaceholder}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        name: value,
                      });
                    }}
                  />
                ) : (
                  // 해외 고객 이름 입력 (이름, 성분리 및 여권 영문 이름)
                  <div className="flex flex-col gap-2">
                    <div
                      className="flex items-center gap-2"
                      style={{
                        // Dr. petit partner의 경우 이름, 성 순서 반대로 표시
                        flexDirection: 'row',
                      }}
                    >
                      <Input
                        style={{
                          width: '100%',
                          height: '48px',
                          borderRadius: '12px',
                        }}
                        value={formData.firstName}
                        placeholder={strings.firstNamePlaceholder}
                        onChange={(value) => {
                          if (alphabetRegex.test(value)) {
                            setFormData({
                              ...formData,
                              firstName: parseFirstName(
                                partner?.id || 0,
                                value,
                              ),
                            });
                          }
                        }}
                      />
                      <Input
                        style={{
                          width: '100%',
                          height: '48px',
                          borderRadius: '12px',
                        }}
                        value={formData.lastName}
                        placeholder={strings.lastNamePlaceHolder}
                        onChange={(value) => {
                          if (alphabetRegex.test(value)) {
                            setFormData({
                              ...formData,
                              lastName: parseLastName(partner?.id || 0, value),
                            });
                          }
                        }}
                      />
                    </div>
                    <Typo type="r-12" color={colors.subTitle}>
                      {strings.passportNameGuideDescription}
                    </Typo>
                  </div>
                )}

                {customerAuthMode === 'BIRTH' && (
                  <div className="flex flex-col gap-2">
                    <Input
                      style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: validateBirthdate(formData.birth || '')
                          ? `1px solid ${colors.red2}`
                          : undefined,
                      }}
                      ref={withMask('99999999', {
                        placeholder: '',
                        showMaskOnFocus: false,
                        showMaskOnHover: false,
                      })}
                      value={formData.birth}
                      placeholder={strings.birth}
                      numberPad
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          birth: value,
                        });
                      }}
                    />
                    {validateBirthdate(formData.birth || '') && (
                      <Typo type="r-12" color={colors.red2}>
                        {validateBirthdate(formData.birth || '')}
                      </Typo>
                    )}

                    <Typo type="r-12" color={colors.subTitle}>
                      {strings.birthInputGuide}
                    </Typo>
                  </div>
                )}

                {customerAuthMode !== 'BIRTH' && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
                      {languageCode && languageCode !== LanguageCode.ko && (
                        <div className="w-full md:w-auto md:min-w-0 md:overflow-hidden">
                          <Select
                            style={{
                              width: '100%',
                              height: '48px',
                              borderRadius: '12px',
                            }}
                            value={countryCode || ''}
                            onChange={(value) => {
                              console.log('value', value);
                              setCountryCode(value as string);
                            }}
                            options={nationOptions}
                          />
                        </div>
                      )}
                      <Input
                        className="flex-shrink-0 md:flex-1"
                        value={formData.phone}
                        ref={phoneInputRef}
                        style={{
                          height: '48px',
                          width: '100%',
                          borderRadius: '12px',
                        }}
                        numberPad
                        placeholder={strings.phonePlaceholder}
                        onChange={(value) => {
                          const numberStrings = filterNumberString(value);
                          setFormData({
                            ...formData,
                            phone:
                              (!countryCode || countryCode === 'KR') &&
                              value.length <= 13
                                ? formatTelNumber(numberStrings)
                                : numberStrings,
                          });
                        }}
                        onFocus={(e) => {
                          e.currentTarget.setSelectionRange(
                            e.currentTarget.value.length,
                            e.currentTarget.value.length,
                          );
                        }}
                      />

                      {!shouldHideAuthRequestButton() && (
                        <Button
                          style={{
                            flexShrink: 0,
                          }}
                          size="small"
                          type="primary"
                          disabled={!canRequestAuth() || requestingAuthNo}
                          onClick={async () => {
                            await requestAuth(formData.phone);
                          }}
                        >
                          {strings.requestAuth}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {customerAuthMode === 'EMAIL' && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Input
                        className="flex-1"
                        style={{
                          height: '48px',
                          borderRadius: '12px',
                        }}
                        value={formData.email || ''}
                        disabled={!!formData.authNo}
                        placeholder={strings.emailPlaceholder}
                        onChange={(value) => {
                          setResendTimer(0);
                          setFormData({
                            ...formData,
                            email: value,
                            authTokenId: '',
                            authNo: '',
                          });
                        }}
                      />
                      <Button
                        style={{
                          flexShrink: 0,
                        }}
                        size="small"
                        type="primary"
                        disabled={
                          !canRequestEmailAuth() ||
                          requestingAuthNo ||
                          resendTimer > 0
                        }
                        onClick={async () => {
                          setFormData((prev) => ({ ...prev, authNo: '' }));
                          await requestAuth(formData.email || '');
                        }}
                      >
                        {resendTimer > 0
                          ? (strings.formatString(
                              strings.resendAuthTimer,
                              resendTimer,
                            ) as string)
                          : formData.authTokenId
                            ? strings.resendAuth
                            : strings.requestAuth}
                      </Button>
                    </div>
                    {!canRequestEmailAuth() && (
                      <Typo type="r-12" color={colors.subTitle}>
                        {strings.validationGuide}
                      </Typo>
                    )}
                  </div>
                )}

                <Input
                  numberPad
                  ref={authNoInputRef}
                  value={formData.authNo}
                  placeholder={strings.authNoPlaceholder}
                  style={{
                    marginTop: 8,
                    width: '100%',
                    height: '48px',
                    borderRadius: '12px',
                    display: formData.authTokenId ? 'initial' : 'none',
                  }}
                  onChange={(value) => {
                    const numberStrings = filterNumberString(value);
                    setFormData({
                      ...formData,
                      authNo: numberStrings,
                    });
                  }}
                />
              </div>
              <div className="flex flex-col">
                <TermsList
                  termsList={termsList}
                  onTermsChange={(isValid) => {
                    setTermsAgreed(isValid);
                  }}
                  onTermsDetailClick={(terms) => {
                    setSelectedTerms(terms);
                    setTermsDetailModalVisible(true);
                  }}
                />
              </div>
              <div className="mt-3">
                <Button
                  disabled={!canSubmit()}
                  style={{
                    width: '100%',
                  }}
                  onClick={async () => {
                    if (
                      customerAuthMode === 'CHATBOT' ||
                      customerAuthMode === 'BIRTH' ||
                      customerAuthMode === 'NONE' ||
                      countryCode === 'CN' ||
                      countryCode === 'cn'
                    ) {
                      await requestChatbotSignIn();
                    } else {
                      await requestSignIn();
                    }
                  }}
                >
                  {strings.confirm}
                </Button>
              </div>
            </div>
          </Sheet.Content>
          {termsDetailModailVisible && (
            <TermsDetailModal
              terms={selectedTerms}
              onCancel={() => {
                setTermsDetailModalVisible(false);
              }}
              visible={termsDetailModailVisible}
            />
          )}
        </Sheet.Container>
        <Sheet.Backdrop
          onTap={() => {
            if (onCancel) onCancel();
          }}
        />
      </Sheet>
    </>
  );
}

export default AuthBottomSheet;
